/**
 * Zone Impact Assessment
 *
 * Calculates the impact of zone inconsistencies on lap times and
 * generates actionable insights for riders.
 */

import {
  type TrackZoneType,
  type ZoneSeverity,
  type ZoneImpact,
  type ZoneVarianceMetrics,
  type ZoneConsensusMetrics,
  type ZoneVarianceDetails,
  type InconsistencyType,
} from './TrackZoneTypes'
import { type ZoneDataPoint } from './ZoneDetection'

// ============================================================================
// Impact Calculation
// ============================================================================

/** Calculate impact assessment for a zone */
export function calculateZoneImpact(
  zoneType: TrackZoneType,
  variance: ZoneVarianceMetrics,
  consensus: ZoneConsensusMetrics,
  zoneLength: number,
  zoneDataPoints?: ZoneDataPoint[][]
): ZoneImpact {
  const estimatedTimeLossMs = estimateTimeLoss(
    variance,
    consensus,
    zoneLength,
    zoneDataPoints
  )
  const severity = determineSeverity(variance.consistencyScore)
  const insight = generateInsight(
    variance.primaryIssue,
    variance.details,
    zoneType,
    estimatedTimeLossMs
  )

  return {
    estimatedTimeLossMs,
    severity,
    rank: 0, // Set during sorting in main analysis
    insight,
  }
}

/**
 * Estimate zone time for a single lap from its data points.
 * Integrates distance/speed across consecutive points.
 */
function estimateZoneTimeMs(dataPoints: ZoneDataPoint[]): number {
  if (dataPoints.length < 2) return 0
  let totalTimeMs = 0
  for (let i = 0; i < dataPoints.length - 1; i++) {
    const dist = dataPoints[i + 1].distance - dataPoints[i].distance
    if (dist <= 0) continue
    // Average speed between consecutive points (km/h → m/s)
    const avgSpeedMs = (dataPoints[i].speed + dataPoints[i + 1].speed) / 2 / 3.6
    if (avgSpeedMs > 0) {
      totalTimeMs += (dist / avgSpeedMs) * 1000
    }
  }
  return totalTimeMs
}

function estimateTimeLoss(
  variance: ZoneVarianceMetrics,
  consensus: ZoneConsensusMetrics,
  zoneLength: number,
  zoneDataPoints?: ZoneDataPoint[][]
): number {
  // Preferred: calculate actual zone times from data points and use the spread
  if (zoneDataPoints && zoneDataPoints.length >= 2) {
    const zoneTimes = zoneDataPoints
      .map(estimateZoneTimeMs)
      .filter((t) => t > 0)

    if (zoneTimes.length >= 2) {
      // Use median vs fastest to estimate typical time loss.
      // This aligns with consistency: if most laps are similar (high consistency),
      // the median will be close to the fastest → small time loss. One outlier
      // lap won't inflate the number, avoiding contradictions like "79% consistent
      // but -0.75s time loss." The zone detail view still shows per-lap deltas
      // so the rider can see the full range there.
      const sorted = [...zoneTimes].sort((a, b) => a - b)
      const fastestTime = sorted[0]
      const medianTime = sorted[Math.floor(sorted.length / 2)]
      const timeLoss = Math.round(medianTime - fastestTime)
      if (timeLoss > 0) return timeLoss
    }
  }

  // Fallback: speed-based estimate
  const avgSpeed = (consensus.avgEntrySpeed + consensus.avgExitSpeed) / 2
  if (avgSpeed <= 0) return 0

  const entryStdDev = variance.details?.entrySpeedVariance?.stdDev ?? 0
  const exitStdDev = variance.details?.exitSpeedVariance?.stdDev ?? 0
  const avgSpeedStdDevKmh = (entryStdDev + exitStdDev) / 2

  if (avgSpeedStdDevKmh <= 0) {
    const zoneTimeMs = (zoneLength / (avgSpeed / 3.6)) * 1000
    return Math.round(zoneTimeMs * variance.speedCV * 0.5)
  }

  const avgSpeedMs = avgSpeed / 3.6
  const speedSpreadMs = (avgSpeedStdDevKmh * 2) / 3.6
  const fastSpeedMs = avgSpeedMs + speedSpreadMs / 2
  const slowSpeedMs = Math.max(avgSpeedMs - speedSpreadMs / 2, 1)
  const fastTimeMs = (zoneLength / fastSpeedMs) * 1000
  const slowTimeMs = (zoneLength / slowSpeedMs) * 1000

  return Math.round(slowTimeMs - fastTimeMs)
}

function determineSeverity(consistencyScore: number): ZoneSeverity {
  if (consistencyScore < 50) return 'high'
  if (consistencyScore < 75) return 'medium'
  return 'low'
}

/** Generate brake release variance note if significant */
function getBrakeReleaseNote(details: ZoneVarianceDetails): string {
  if (!details.brakeReleaseVariance?.isSignificant) return ''
  const range = details.brakeReleaseVariance.range
  return range > 0
    ? ` Release point also varies by ${Math.round(range)}m.`
    : ' Release point also varies.'
}

/** Format time saved as a short note, empty if negligible */
function getTimeSavedNote(ms: number): string {
  if (ms < 100) return ''
  return ` Worth ~${(ms / 1000).toFixed(1)}s.`
}

// ============================================================================
// Insight Generation
// ============================================================================

/** Generate actionable insight based on specific inconsistency type */
function generateInsight(
  primaryIssue: InconsistencyType,
  details: ZoneVarianceDetails | undefined,
  zoneType: TrackZoneType,
  timeLossMs: number
): string {
  if (!primaryIssue) {
    return 'Good consistency here. Ready for controlled experiments — change one thing at a time to find improvement.'
  }

  if (!details) {
    return generateFallbackInsight(zoneType)
  }

  // Check for correlated issues that explain the primary issue
  const correlatedInsight = checkForCorrelatedIssues(
    primaryIssue,
    details,
    zoneType,
    timeLossMs
  )
  if (correlatedInsight) {
    return correlatedInsight
  }

  return generateSpecificInsight(primaryIssue, details, zoneType, timeLossMs)
}

/**
 * Check for correlated issues that explain the root cause.
 * E.g., if entry speed AND brake point both vary, the brake point is likely the root cause.
 */
function checkForCorrelatedIssues(
  primaryIssue: InconsistencyType,
  details: ZoneVarianceDetails,
  zoneType: TrackZoneType,
  timeLossMs: number
): string | null {
  const timeSaved = getTimeSavedNote(timeLossMs)

  // Entry speed + brake point both vary
  if (
    primaryIssue === 'entry-speed' &&
    details.brakingPointVariance?.isSignificant &&
    zoneType === 'braking'
  ) {
    const bpRange = details.brakingPointVariance.range
    const speedRange = details.entrySpeedVariance?.range ?? 0
    return `Brake point moves ${Math.round(bpRange)}m, swinging entry speed by ${Math.round(speedRange)} km/h.${getBrakeReleaseNote(details)} Same brake marker every lap — once consistent, try adjusting.${timeSaved}`
  }

  // Exit speed + throttle point → throttle point is likely the cause
  if (
    primaryIssue === 'exit-speed' &&
    details.throttlePointVariance?.isSignificant
  ) {
    const tpRange = details.throttlePointVariance.range
    const speedRange = details.exitSpeedVariance?.range ?? 0
    return `Throttle pickup moves ${Math.round(tpRange)}m, swinging exit speed by ${Math.round(speedRange)} km/h. Same rollout point every lap — once consistent, try earlier throttle.${timeSaved}`
  }

  // Exit speed + entry speed both vary → upstream issue
  if (
    primaryIssue === 'exit-speed' &&
    details.entrySpeedVariance?.isSignificant
  ) {
    const entryRange = details.entrySpeedVariance.range
    const exitRange = details.exitSpeedVariance?.range ?? 0
    return `Exit varies ${Math.round(exitRange)} km/h, driven by ${Math.round(entryRange)} km/h entry variation. Get the entry consistent first — the exit will follow.`
  }

  // Apex location + apex speed both vary → line inconsistency
  if (
    primaryIssue === 'apex-speed' &&
    details.apexLocationVariance?.isSignificant
  ) {
    const locRange = details.apexLocationVariance.range
    return `Slowest point wanders ${Math.round(locRange)}m, changing mid-corner speed. Same line every lap — once locked in, experiment with the apex to find time.`
  }

  // Braking intensity + entry speed both vary
  if (
    primaryIssue === 'braking-intensity' &&
    details.entrySpeedVariance?.isSignificant
  ) {
    const speedRange = details.entrySpeedVariance.range
    return `Braking force varies because entry speed swings ${Math.round(speedRange)} km/h.${getBrakeReleaseNote(details)} Consistent entry first — the braking pressure follows naturally.`
  }

  return null
}

function generateSpecificInsight(
  issue: Exclude<InconsistencyType, null>,
  details: ZoneVarianceDetails,
  _zoneType: TrackZoneType,
  timeLossMs: number
): string {
  const timeSaved = getTimeSavedNote(timeLossMs)

  const insightGenerators: Record<
    Exclude<InconsistencyType, null>,
    () => string
  > = {
    'braking-point': () => {
      const bpRange = details.brakingPointVariance?.range ?? 0
      return `Brake point moves ${Math.round(bpRange)}m between laps.${getBrakeReleaseNote(details)} Same marker every lap — once repeatable, try adjusting.${timeSaved}`
    },

    'braking-intensity': () => {
      const range = details.brakingIntensityVariance?.range ?? 0
      return `Braking force varies ${range.toFixed(1)}g between laps.${getBrakeReleaseNote(details)} Same initial squeeze every lap — builds feel and makes changes measurable.`
    },

    'entry-speed': () => {
      const range = details.entrySpeedVariance?.range ?? 0
      return `Entry speed varies ${Math.round(range)} km/h — often carries from the previous zone. Get consistent there first and this settles.`
    },

    'apex-speed': () => {
      const range = details.apexSpeedVariance?.range ?? 0
      return `Mid-corner speed varies ${Math.round(range)} km/h. Same line every lap — once consistent, experiment with carrying more speed.`
    },

    'apex-location': () => {
      const range = details.apexLocationVariance?.range ?? 0
      return `Slowest point wanders ${Math.round(range)}m. Repeat the same line every lap — once locked in, try adjusting to find time.`
    },

    'throttle-point': () => {
      const range = details.throttlePointVariance?.range ?? 0
      return `Throttle pickup moves ${Math.round(range)}m. Same rollout each lap — once repeatable, try earlier throttle for more exit speed.${timeSaved}`
    },

    'throttle-intensity': () => {
      const range = details.throttleIntensityVariance?.range ?? 0
      return `Throttle application varies ${range.toFixed(1)}g. Smooth, repeatable pickup builds grip confidence and makes improvements measurable.`
    },

    'coasting-duration': () => {
      const range = details.coastingVariance?.distanceRange ?? 0
      return `Coasting varies ${Math.round(range)}m. Close the gap between brake and throttle consistently — the bike is most settled under load.`
    },

    'exit-speed': () => {
      const range = details.exitSpeedVariance?.range ?? 0
      return `Exit speed varies ${Math.round(range)} km/h — usually driven by entry. Get the entry consistent first, the exit follows.`
    },
  }

  const generator = insightGenerators[issue]
  return generator ? generator() : generateFallbackInsight(_zoneType)
}

function generateFallbackInsight(zoneType: TrackZoneType): string {
  const fallbacks: Record<TrackZoneType, string> = {
    braking:
      'Braking varies between laps. Same brake marker every lap — once repeatable, try adjusting.',
    cornering:
      'Corner approach varies between laps. Same line every lap — once consistent, experiment to find time.',
    acceleration:
      'Exit varies between laps. Same throttle point every lap — once repeatable, try rolling on earlier.',
    straight:
      'Some variation detected — usually linked to how the previous corner is exited.',
  }

  return fallbacks[zoneType]
}
